/**
 * Devices — query hooks.
 *
 * Read-only tenant device inventory. Backed by
 * internal/identity/device.Handler.ListForTenant, mounted at
 * /api/v1/tenants/{tenant_id}/devices via WithPathTenant.
 */

import { useQuery } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  devicesListResponseSchema,
  type DevicesListResponse,
  type DevicesListSearch,
} from "./schemas";

export async function fetchDevices(
  tenantID: string,
  search: DevicesListSearch,
): Promise<DevicesListResponse> {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/devices`,
    {
      searchParams: {
        q: search.q,
        device_type: search.deviceType,
        status: search.status,
        user_id: search.userId,
        offset: search.offset,
      },
    },
  );
  return devicesListResponseSchema.parse(raw);
}

export function useDevicesQuery(tenantID: string, search: DevicesListSearch) {
  return useQuery<DevicesListResponse, ApiError>({
    queryKey: queryKeys.devices.list(tenantID, search),
    queryFn: () => fetchDevices(tenantID, search),
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}
